/**
 * F-296 / T-232-002 — Lead scoring FE service.
 *
 * Consumes the canonical LP lead-scoring pipeline (mounted in src/app.js at
 * ['/api/lead-scoring','/lead-scoring'], route table in src/routes/lead-scoring.js,
 * engine in src/lib/lead-scoring.js):
 *   GET  /api/lead-scoring/leads?tenantId=<id>  -> admin, { success, data: Lead[] }
 *   GET  /api/lead-scoring/meta                 -> open,  { success, data: { maxScore, weights, tiers, segments } }
 *   POST /api/lead-scoring/score                -> pure,  { success, data: { score, breakdown, tier } }
 *   POST /api/lead-scoring/engagement           -> server-side OPEN/CLICK rescore (src/routes/webhooks.js)
 *
 * The BE lead shape is { leadId, tenantId, email, source, propertyCount,
 * segment, location: { city, state }, engagement, score, breakdown, tier,
 * createdAt, updatedAt }. The FE keeps its defensive degrades and tolerates
 * schema drift so this surface stays up while the engine warms up.
 */
import { adminApi } from './api';

export type LeadTier = 'HOT' | 'WARM' | 'COLD' | string;

export interface LeadScore {
    id: string;
    leadId?: string;
    tenantId?: string;
    email?: string;
    name?: string;
    company?: string;
    source?: string;
    propertyCount?: number;
    segment?: string;
    jurisdiction?: string;
    location?: { city?: string; state?: string };
    engagement?: { openedCount?: number; clickCount?: number; lastEngagedAt?: string };
    score: number;
    tier?: LeadTier;
    breakdown?: Record<string, unknown>;
    createdAt?: string;
    updatedAt?: string;
    [key: string]: unknown;
}

export interface LeadScoringSnapshot {
    total: number;
    averageScore: number;
    bands: Array<{ label: string; count: number }>;
    leads: LeadScore[];
}

export interface ScoreBand {
    label: string;
    min: number;
    max: number;
}

// BE tier thresholds (lib/lead-scoring.js LEAD_SCORE_TIERS): HOT >= 70, WARM >= 40, COLD < 40.
export const SCORE_BANDS: ScoreBand[] = [
    { label: 'Hot', min: 70, max: 100 },
    { label: 'Warm', min: 40, max: 69 },
    { label: 'Cold', min: 0, max: 39 },
];

export const clampScore = (s: unknown): number => {
    const n = Number(s);
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
    return 0;
};

export function bandOf(score: unknown): string {
    const s = clampScore(score);
    return s >= 70 ? 'Hot' : s >= 40 ? 'Warm' : 'Cold';
}

/** Prefer the engine-assigned tier; fall back to the score band. */
export function tierOf(lead: LeadScore): string {
    if (lead.tier) {
        const t = String(lead.tier).toUpperCase();
        if (t === 'HOT') return 'Hot';
        if (t === 'WARM') return 'Warm';
        if (t === 'COLD') return 'Cold';
    }
    return bandOf(lead.score);
}

export function summarizeLeads(leads: LeadScore[]): LeadScoringSnapshot {
    const total = leads.length;
    const chunks: Array<{ label: string; count: number }> = SCORE_BANDS.map((b) => ({ label: b.label, count: 0 }));
    let sum = 0;
    for (const lead of leads) {
        sum += clampScore(lead.score);
        const band = tierOf(lead);
        const hit = chunks.find((c) => c.label === band);
        if (hit) hit.count += 1;
    }
    return {
        total,
        averageScore: total ? sum / total : 0,
        bands: chunks,
        leads,
    };
}

const unwrap = (res: any, label: string): any => {
    const body = res?.data;
    if (body && body.success === false) {
        throw new Error(body.message || `${label} failed`);
    }
    return body && body.data !== undefined ? body.data : body;
};

const unwrapLeads = (res: any): LeadScore[] => {
    const data = unwrap(res, 'lead scoring');
    const rows = Array.isArray(data) ? data : data && Array.isArray(data.leads) ? data.leads : [];
    return rows.map((row: any, idx: number) => {
        const location = row.location || {};
        return {
            id: row.leadId ? String(row.leadId) : row.id ? String(row.id) : `lead-${idx}`,
            leadId: row.leadId,
            tenantId: row.tenantId,
            email: row.email,
            name: row.name,
            company: row.company,
            source: row.source,
            propertyCount: row.propertyCount,
            segment: row.segment,
            jurisdiction: row.jurisdiction || [location.city, location.state].filter(Boolean).join(', '),
            location,
            engagement: row.engagement,
            score: clampScore(row.score),
            tier: row.tier,
            breakdown: row.breakdown,
            createdAt: row.createdAt,
            updatedAt: row.updatedAt,
        };
    });
};

/**
 * Resolve the signed-in account's tenant scope from the Cognito idToken
 * (custom:tenantId claim, set at sign-up in services/cognito.ts). The BE
 * /api/lead-scoring/leads route is admin+tenant-scoped via ?tenantId=.
 */
export function currentTenantId(): string | undefined {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('idToken') : null;
    if (!token) return undefined;
    try {
        const claims = JSON.parse(atob(token.split('.')[1]));
        return claims['custom:tenantId'] || claims.tenantId || claims['custom:accountId'] || undefined;
    } catch {
        return undefined;
    }
}

export const leadScoringApi = {
    getScores: (): Promise<LeadScore[]> => {
        const tenantId = currentTenantId();
        if (!tenantId) {
            // No signed-in tenant scope yet -> nothing to surface.
            return Promise.resolve([]);
        }
        return adminApi
            .get('api/lead-scoring/leads', { params: { tenantId } })
            .then((r: any) => unwrapLeads(r));
    },

    getSummary: (): Promise<LeadScoringSnapshot> =>
        leadScoringApi.getScores().then((leads) => summarizeLeads(leads)),

    getMeta: (): Promise<{ maxScore: number; weights: Record<string, number>; tiers: Record<string, number>; segments: string[] }> =>
        adminApi.get('api/lead-scoring/meta').then((r: any) => unwrap(r, 'lead scoring meta')),

    scoreLead: (factors: any, engagement?: any): Promise<{ score: number; tier: LeadTier; breakdown: Record<string, unknown> }> =>
        adminApi
            .post('api/lead-scoring/score', { factors, engagement })
            .then((r: any) => {
                const data = unwrap(r, 'score lead');
                return {
                    score: clampScore(data?.score),
                    tier: data?.tier,
                    breakdown: data?.breakdown || data?.weights || {},
                };
            }),
};

export default leadScoringApi;